import React, {
  useCallback,
  useEffect,
  SetStateAction,
  Dispatch,
  useState,
} from "react";
import {
  MotifIcon,
  MotifVerticalNavigation,
  MotifVerticalNavigationContent,
  MotifVerticalNavigationMenu,
  MotifVerticalNavigationMenuItem,
  MotifVerticalNavigationSubmenu,
} from "@/libs/@ey-xd/motif-react";
import { IconoirFastArrowLeft, IconoirFastArrowRight } from "@ey-xd/motif-icon";
import menuDataAll from "@/menuData";
import "@/index.scss";
import "./navbar.scss";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useCase } from "@/features/case/use-case";
import { havingEditPermission } from "@/utility/common";
import UserAvatar from "@/features/auth/user-avatar";
import { MOBILE_BREAKPOINT } from "@/Constant";

interface NavBarProps {
  collapse: boolean;
  setCollapse: Dispatch<SetStateAction<boolean>>;
}

const NavBar: React.FC<NavBarProps> = React.memo(
  ({ collapse, setCollapse }) => {
    const user = useSelector((state: any) => state.user);
    const location = useLocation();
    const showAvatar =
      !!user?.accessToken || !location.pathname.includes("logout");
    const params: any = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { caseId } = useSelector((state: any) => state.case);
    const permissions = useSelector((state: any) => state.permissions);

    let isBasicUser = false;
    const { User: UserPermission } = useSelector(
      (state: any) => state.appPermissions
    );

    if (!UserPermission || !havingEditPermission(UserPermission)) {
      isBasicUser = true;
    }
    const menuData = menuDataAll.filter((menu) => {
      if (menu.path === "/user-management" && isBasicUser) {
        return false;
      }
      return true;
    });
    const [screenWidth, setScreenWidth] = useState(window.outerWidth);

    useEffect(() => {
      window.addEventListener("resize", () => {
        setScreenWidth(window.outerWidth);
      });
      return () => window.removeEventListener("resize", () => {});
    }, []);

    let paramsData = params?.["*"] ? params?.["*"] : "";
    let caseParamsArray = paramsData.split("/");
    let caseIdFromParams = "";
    if (caseParamsArray.length > 1 && paramsData.includes("case")) {
      caseIdFromParams = caseParamsArray[1];
    }
    const caseData = useCase(caseIdFromParams, !caseId);

    const [selectedSubmenu, setSelectedSubmenu] = React.useState<number | null>(
      null
    );
    const handleMenuClick = useCallback(
      (menuIndex: number, subMenuIndex: number | null = null) => {
        let link = menuData[menuIndex].path;

        if (subMenuIndex !== null && subMenuIndex >= 0 && caseId) {
          link = `/case/${caseId}?activeTab=${subMenuIndex}`;
          setSelectedSubmenu(subMenuIndex);
        } else if (
          subMenuIndex !== null &&
          subMenuIndex >= 0 &&
          (caseIdFromParams || caseData?.caseId) &&
          !caseId
        ) {
          let getCaseId = caseData?.caseId || caseIdFromParams;
          link = `/case/${getCaseId}?activeTab=${subMenuIndex}`;
          setSelectedSubmenu(subMenuIndex);
        } else if (link === "/case") {
          link = "/";
        }

        // non-relative links that do not contain ey.com open in new tab
        if (!link.includes("ey.com") && !link.startsWith("/")) {
          window.open(
            menuData[menuIndex].path,
            "_blank",
            "noopener noreferrer"
          );
          return;
        }
        navigate(link);
        if (screenWidth <= MOBILE_BREAKPOINT) {
          setCollapse(true);
        }
      },
      [
        menuData,
        caseId,
        caseIdFromParams,
        caseData?.caseId,
        navigate,
        setCollapse,
        screenWidth,
      ]
    );

    useEffect(() => {
      const isCaseRoute =
        location.pathname.includes("case") &&
        !location.pathname.includes("/case-setup") &&
        permissions.hasCasePermission;
      const hasActiveTab = location.search.includes("activeTab");

      if (isCaseRoute || hasActiveTab) {
        let submenuIndex: number = 0;
        const caseRouteBase = location.pathname.substring(42);

        if (caseRouteBase.length > 2) {
          let found = false;
          menuData[1].subMenus?.forEach((subMenu, subIndex) => {
            if (found) return;
            subMenu.matchPath?.forEach((path) => {
              if (
                caseRouteBase.includes(path) &&
                caseRouteBase.indexOf(path) < 5
              ) {
                submenuIndex = subIndex;
                found = true;
              }
            });
          });
        } else {
          submenuIndex = parseInt(location.search.split("=")[1]) || 0;
        }

        const menuElement = document.querySelector(".sidebar-nav-menu");
        const caseMenuItem = menuElement?.children[1] as HTMLElement;
        if (
          isCaseRoute &&
          caseMenuItem &&
          !caseMenuItem.className.includes("open") &&
          !collapse
        ) {
          caseMenuItem.click();
        }

        const submenuItem = document.querySelector(
          `.sidebar-nav-submenu-item:nth-child(${submenuIndex + 1})`
        );
        submenuItem?.scrollIntoView({ behavior: "smooth" });

        setSelectedSubmenu(submenuIndex);
      }
    }, [
      location.pathname,
      location.search,
      collapse,
      menuData,
      permissions.hasCasePermission,
    ]);
    if (
      !(
        user &&
        user?.applicationAccess &&
        user.accessToken &&
        !location.pathname.includes("logout")
      )
    ) {
      return screenWidth <= MOBILE_BREAKPOINT ? (
        <div className="intial-user-avatar-load-for-mobile-view">
          {showAvatar && <UserAvatar />}
        </div>
      ) : (
        <></>
      );
    }
    return (
      <>
        <MotifVerticalNavigation
          className="sidebar motif-no-padding"
          collapse={collapse}
        >
          <div className="sidebar-nav-collapse-button">
            <button
              onClick={() => setCollapse(!collapse)}
              className="motif-button motif-button-primary"
            >
              {collapse ? (
                <MotifIcon src={IconoirFastArrowRight} />
              ) : (
                <>
                  <MotifIcon src={IconoirFastArrowLeft} />{" "}
                  <span className="motif-body2-underlined-regular">
                    {" "}
                    {t("pages.navBar.collapse")}
                  </span>
                </>
              )}
            </button>
          </div>
          <MotifVerticalNavigationContent className="sidebar-nav-content">
            <MotifVerticalNavigationMenu className="sidebar-nav-menu">
              {menuData.map((route, menuIndex) => {
                if (
                  location.pathname.includes("/case") &&
                  !location.pathname.includes("/case-setup") &&
                  permissions.hasCasePermission &&
                  route.subMenus &&
                  route.subMenus.length > 0
                ) {
                  return (
                    <MotifVerticalNavigationSubmenu
                      className="sidebar-nav-menu-item sidebar-nav-submenu"
                      key={`route-0-${menuIndex}`}
                      icons={{ title: route.icon, expandToggle: null }}
                      selected={location.pathname.includes(route.path)}
                      title={t(route.label)}
                    >
                      {route.subMenus.map((subRoute, subMenuIndex) => {
                        return (
                          <MotifVerticalNavigationMenuItem
                            className={`sidebar-nav-submenu-item  ${
                              collapse ? "selectedIteam" : ""
                            }`}
                            key={`route-1-${subMenuIndex}`}
                            selected={selectedSubmenu === subMenuIndex}
                            onClick={() =>
                              handleMenuClick(menuIndex, subMenuIndex)
                            }
                          >
                            {t(subRoute.label)}
                          </MotifVerticalNavigationMenuItem>
                        );
                      })}
                    </MotifVerticalNavigationSubmenu>
                  );
                }

                if (
                  menuIndex === 1 &&
                  (!location.pathname.includes("case") ||
                    location.pathname.includes("/case-setup") ||
                    !permissions.hasCasePermission)
                ) {
                  return "";
                }

                return (
                  <MotifVerticalNavigationMenuItem
                    className={`sidebar-nav-menu-item `}
                    key={route.path}
                    icon={route.icon}
                    selected={location?.pathname === route.path}
                    onClick={(event) => handleMenuClick(menuIndex)}
                    label={t(route.label)}
                  >
                    {t(route.label)}
                  </MotifVerticalNavigationMenuItem>
                );
              })}
            </MotifVerticalNavigationMenu>
          </MotifVerticalNavigationContent>
          <div className="nav-user-profile">
            {showAvatar && screenWidth < MOBILE_BREAKPOINT && <UserAvatar />}
          </div>
        </MotifVerticalNavigation>
      </>
    );
  }
);

export default NavBar;
